import { LicenseFeature } from '../school-license/models/license-feature.model';

export type ReleaseNoteKind = 'new' | 'improvement' | 'fix';

/** One change, as a short Czech sentence. */
export interface ReleaseNoteItem {
  text: string;
  kind: ReleaseNoteKind;
  /**
   * The restricted feature the change belongs to: its tiers are shown as badges and its link only
   * to a school that has it. Omitted when every plan has the change.
   */
  feature?: LicenseFeature;
  /** Where to try the change out or read about it: an app route without params. */
  link?: { path: string; label: string };
}

/** One release worth telling users about: a date and a hand-written list of what changed. */
export interface ReleaseNote {
  /** Release date as an ISO date; doubles as the version stored once the user has seen it. */
  version: string;
  items: ReleaseNoteItem[];
}

/**
 * Release notes, newest first. They are written by hand — the list is what users see, not a
 * changelog — and adding an entry on top is the whole release: the next time a signed-in user
 * opens the app they get the entries newer than the last version they saw, once. Every entry
 * stays listed on the Co je nového page. Features only system admins can use never go here.
 */
export const RELEASE_NOTES: readonly ReleaseNote[] = [
  {
    version: '2026-10-15',
    items: [
      {
        kind: 'new',
        text: 'Deník výuky: v záložce Deník u tematického plánu zapisujete, jak výuka jednotlivých bloků proběhla. Záznam sdílíte s celou školou, nebo ho můžete nastavit jako soukromý.',
      },
      {
        kind: 'new',
        text: 'Nový kurz Jak a proč vést Deník výuky: co do deníku zapisovat, aby záznamy pomohly při další výuce tematického bloku. Najdete ho na úvodní stránce mezi kurzy.',
        feature: 'diaryCourse',
        link: { path: '/jak-vest-denik-vyuky', label: 'Otevřít kurz' },
      },
      {
        kind: 'new',
        text: 'Analýza OVU nově pod každým grafem doporučí, co upravit: OVU, která nejsou zařazena v žádném bloku, a OVU zařazená ve výrazně více blocích než ostatní. K doporučení Mapa výuky najde tematické bloky, které se k OVU obsahově hodí nejvíc, nebo bloky, ze kterých lze OVU odebrat. U kteréhokoli bloku vám AI na požádání zdůvodní, proč se k OVU hodí nebo nehodí. Tato funkce čerpá AI kredit školy. Je-li vysoké využití OVU záměrné, doporučení skryjete pro všechny, kdo na ŠVP pracují, a kdykoli ho můžete znovu zobrazit.',
      },
      {
        kind: 'new',
        text: 'Nový průvodce Jak postupovat při analýze OVU vás provede čtením grafů i prací s doporučeními.',
        link: { path: '/jak-na-analyzu-ovu', label: 'Průvodce analýzou OVU' },
      },
      {
        kind: 'new',
        text: 'Uživatelé školy si mohou psát: v seznamu uživatelů školy napíšete zprávu jednomu kolegovi, vybraným nebo všem. Zpráva přijde e-mailem a odpověď na ni dorazí do vaší e-mailové schránky.',
      },
      {
        kind: 'new',
        text: 'Skupiny volitelných předmětů: tlačítkem Skupiny volitelných předmětů na stránce Předměty vytvoříte skupinu předmětů, ze kterých žák navštěvuje jen některé, a zadáte jejich počet. Učební plán pak do týdenní hodinové dotace ročníku započítá ze skupiny jen tolik předmětů, kolik jich žák navštěvuje, a stejně počítají i exporty. Skupina má v učebním plánu jeden řádek a hodiny zadané v něm platí pro každý její předmět. Do skupiny lze zařadit předměty s disponibilní časovou dotací.',
      },
      {
        kind: 'improvement',
        text: 'Nastavíte-li v učebním plánu předmětu v ročníku 0 hodin a ročník už má tematické bloky, Mapa výuky vás před uložením upozorní, že se bloky smažou. Pokud je chcete zachovat, učební plán se neuloží a hodiny můžete opravit.',
      },
    ],
  },
  {
    version: '2026-09-25',
    items: [
      {
        kind: 'new',
        text: 'Nastavení AI: správce školy si u své školy zvolí model, míru kreativity, úroveň přemýšlení a vlastní pokyny pro generování textů.',
        feature: 'aiSettings',
      },
    ],
  },
  {
    version: '2026-09-23',
    items: [
      {
        kind: 'improvement',
        text: 'Pořadí předmětů v ŠVP si nyní nastavíte přetažením řádku v seznamu předmětů.',
      },
      {
        kind: 'improvement',
        text: 'Export ŠVP do dokumentu má nová nastavení: můžete vypnout titulní stranu, číslování stran nebo učivo.',
      },
      {
        kind: 'new',
        text: 'Administrátor školy vidí v přehledu licence statistiku využití AI asistenta po uživatelích a dnech.',
      },
      {
        kind: 'new',
        text: 'Program doporučení: škola s placenou licencí může doporučit Mapu výuky jiné škole a obě získají slevu na příští licenci.',
      },
      {
        kind: 'improvement',
        text: 'Chybové hlášky jsou srozumitelnější a vždy vysvětlují, co se stalo a co s tím.',
      },
      {
        kind: 'new',
        text: 'Přidali jsme podmínky užívání. Každý uživatel je odsouhlasí jednou po přihlášení; jsou k nahlédnutí i bez přihlášení na stránce Podmínky užívání.',
        link: { path: '/podminky-uzivani', label: 'Podmínky užívání' },
      },
    ],
  },
];

/** The version of the newest entry — what a user has seen once they close the dialog. */
export const RELEASE_NOTES_VERSION = RELEASE_NOTES[0].version;

/**
 * The entries that are news to the user, newest first: released after the last version this
 * browser saw (`seenVersion`, or nothing yet) and after the account was created (`createdAt`,
 * an ISO date-time or unknown) — a user who joined next year has not missed this year's releases.
 */
export function unseenReleaseNotes(
  seenVersion: string | null,
  createdAt: string | undefined,
): ReleaseNote[] {
  // ISO dates compare as strings; the day of the account's creation counts as before it.
  const createdOn = createdAt?.slice(0, 10) ?? null;
  return RELEASE_NOTES.filter(
    (note) =>
      (seenVersion === null || note.version > seenVersion) &&
      (createdOn === null || note.version > createdOn),
  );
}
